import React, { useMemo } from 'react';

const MAX_TWEET_LENGTH = 280;

// Parse numbered thread format (e.g. "1/5 Hook text\n\n2/5 Detail...") into individual tweet texts
function parseThread(body) {
  if (!body || !body.trim()) return [];
  const parts = body.split(/^\d+\/\d+\s/m);
  // First element is always empty (before "1/N"), skip it. Keep empty tweets so users can type in them.
  return parts.slice(1).map(t => t.replace(/\n+$/, ''));
}

// Serialize tweets back to numbered format
function serializeThread(tweets) {
  const total = tweets.length;
  return tweets.map((t, i) => `${i + 1}/${total} ${t}`).join('\n\n');
}

const TweetThreadEditor = ({ body, onBodyChange, onSave, onPostThread, postingThread, saving, dirty }) => {
  const tweets = useMemo(() => {
    const parsed = parseThread(body);
    return parsed.length > 0 ? parsed : [body?.trim() || ''];
  }, [body]);

  const updateTweet = (index, newText) => {
    const updated = [...tweets];
    updated[index] = newText;
    onBodyChange(serializeThread(updated));
  };

  const moveTweet = (index, direction) => {
    const newIndex = index + direction;
    if (newIndex < 0 || newIndex >= tweets.length) return;
    const updated = [...tweets];
    [updated[index], updated[newIndex]] = [updated[newIndex], updated[index]];
    onBodyChange(serializeThread(updated));
  };

  const deleteTweet = (index) => {
    if (tweets.length <= 1) return;
    const updated = tweets.filter((_, i) => i !== index);
    onBodyChange(serializeThread(updated));
  };

  const addTweet = (afterIndex) => {
    const updated = [...tweets];
    updated.splice(afterIndex + 1, 0, '');
    onBodyChange(serializeThread(updated));
  };

  const hasOverLength = tweets.some(t => t.length > MAX_TWEET_LENGTH);

  return (
    <section className="bg-gray-800 border border-gray-700 rounded-lg p-4 space-y-3">
      <div className="flex justify-between items-center">
        <h3 className="text-sm font-medium text-gray-300">
          Thread Editor ({tweets.length} tweet{tweets.length !== 1 ? 's' : ''})
        </h3>
        <div className="flex gap-2">
          <button
            onClick={onSave}
            disabled={saving || !dirty}
            className="bg-blue-600 hover:bg-blue-700 disabled:bg-gray-600 disabled:cursor-not-allowed text-white px-3 py-1.5 rounded-lg text-xs transition-colors"
          >
            {saving ? 'Saving...' : 'Save Thread'}
          </button>
          <button
            onClick={onPostThread}
            disabled={postingThread || hasOverLength || tweets.length === 0}
            className="bg-green-600 hover:bg-green-700 disabled:bg-gray-600 disabled:cursor-not-allowed text-white px-3 py-1.5 rounded-lg text-xs transition-colors"
          >
            {postingThread ? 'Posting...' : 'Post Thread'}
          </button>
        </div>
      </div>

      {hasOverLength && (
        <p className="text-red-400 text-xs">One or more tweets exceed 280 characters. Fix before posting.</p>
      )}

      <div className="space-y-2">
        {tweets.map((tweet, index) => {
          const charCount = tweet.length;
          const isOver = charCount > MAX_TWEET_LENGTH;

          return (
            <div key={index}>
              <div className="flex gap-2">
                {/* Reorder + Delete */}
                <div className="flex flex-col gap-1 pt-1">
                  <button
                    onClick={() => moveTweet(index, -1)}
                    disabled={index === 0}
                    className="text-gray-500 hover:text-gray-300 disabled:opacity-30 text-xs leading-none"
                    title="Move up"
                  >
                    &#9650;
                  </button>
                  <button
                    onClick={() => moveTweet(index, 1)}
                    disabled={index === tweets.length - 1}
                    className="text-gray-500 hover:text-gray-300 disabled:opacity-30 text-xs leading-none"
                    title="Move down"
                  >
                    &#9660;
                  </button>
                  <button
                    onClick={() => deleteTweet(index)}
                    disabled={tweets.length <= 1}
                    className="text-red-500 hover:text-red-400 disabled:opacity-30 text-xs leading-none mt-1"
                    title="Delete tweet"
                  >
                    &#10005;
                  </button>
                </div>

                {/* Tweet content */}
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-xs text-gray-500 font-mono">{index + 1}/{tweets.length}</span>
                    <span className={`text-xs font-mono ${isOver ? 'text-red-400' : charCount > 260 ? 'text-yellow-400' : 'text-gray-500'}`}>
                      {charCount}/{MAX_TWEET_LENGTH}
                    </span>
                  </div>
                  <textarea
                    rows="3"
                    value={tweet}
                    onChange={(e) => updateTweet(index, e.target.value)}
                    className={`w-full px-3 py-2 bg-gray-900 border rounded-lg text-gray-100 text-sm font-mono focus:outline-none resize-y ${
                      isOver ? 'border-red-500 focus:border-red-400' : 'border-gray-600 focus:border-blue-500'
                    }`}
                    placeholder={`Tweet ${index + 1}...`}
                  />
                </div>
              </div>

              {/* Add tweet button between cards */}
              <div className="flex justify-center my-1">
                <button
                  onClick={() => addTweet(index)}
                  className="text-gray-600 hover:text-gray-400 text-xs transition-colors"
                  title="Add tweet after this one"
                >
                  + add tweet
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
};

export default TweetThreadEditor;
